import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  RefreshCw, Search, CheckCircle, AlertTriangle, XCircle, Clock,
  ChevronLeft, ChevronRight, Play, FileText, BarChart3, TrendingUp, TrendingDown, MapPin
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReconciliationDashboard() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <DashboardLayout title="Admin Panel" user={user} portalColor="red">
      <div className="p-4 space-y-4" data-testid="reconciliation-page">
        <h1 className="text-2xl font-bold text-gray-900">Reconciliation & Intelligence</h1>
        <div className="flex gap-1 border-b pb-0">
          {[
            { key: "dashboard", label: "Overview", icon: BarChart3 },
            { key: "reconcile", label: "Reconciliation", icon: CheckCircle },
            { key: "lanes", label: "Lane Intelligence", icon: MapPin },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === t.key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}
              data-testid={`recon-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        <div className="bg-white border rounded-lg p-4">
          {activeTab === "dashboard" && <OverviewTab headers={headers} />}
          {activeTab === "reconcile" && <ReconcileTab headers={headers} token={token} />}
          {activeTab === "lanes" && <LanesTab headers={headers} token={token} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

function OverviewTab({ headers }) {
  const [reconStats, setReconStats] = useState(null);
  const [laneStats, setLaneStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/v2/reconciliation/stats`, { headers }).then(r => r.json()).then(setReconStats);
    fetch(`${API}/api/v2/lane-intelligence/stats`, { headers }).then(r => r.json()).then(setLaneStats);
  }, []);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Reconciliation Stats */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-3">Reconciliation Summary</h3>
        {reconStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Total Shipments" value={reconStats.total_shipments} />
            <StatCard label="Reconciled" value={reconStats.total_reconciliations} color="blue" />
            <StatCard label="Matched" value={reconStats.by_status?.MATCHED?.count || 0} color="green" />
            <StatCard label="Pending" value={reconStats.by_status?.PENDING?.count || 0} color="amber" />
            <StatCard label="Disputed" value={reconStats.by_status?.DISPUTED?.count || 0} color="red" />
          </div>
        )}
      </div>
      {/* Lane Intelligence Stats */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-3">Lane Intelligence</h3>
        {laneStats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <StatCard label="Total Lanes" value={laneStats.total_lanes} />
              <StatCard label="Profitable" value={laneStats.profitable_lanes} color="green" />
              <StatCard label="Loss Making" value={laneStats.loss_making_lanes} color="red" />
              <StatCard label="Avg Margin" value={`${laneStats.avg_margin}%`} color="blue" />
              <StatCard label="Total Revenue" value={`₹${(laneStats.total_revenue || 0).toLocaleString()}`} color="green" />
            </div>
            {laneStats.top_profitable?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1"><TrendingUp className="w-4 h-4 text-green-600" /> Top Profitable Lanes</h4>
                <div className="grid gap-2 md:grid-cols-3">
                  {laneStats.top_profitable.map((l, i) => (
                    <div key={i} className="border rounded p-2 text-xs bg-green-50">
                      <div className="font-medium">{l.origin_city} → {l.destination_city}</div>
                      <div className="text-gray-500">{l.shipment_count} shipments | Profit: ₹{l.profit} ({l.profit_margin_percent}%)</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = "gray" }) {
  const colors = {
    gray: "text-gray-900", green: "text-green-600", red: "text-red-600",
    blue: "text-blue-600", amber: "text-amber-600"
  };
  return (
    <div className="border rounded p-3 text-center">
      <div className={`text-xl font-bold ${colors[color]}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function ReconcileTab({ headers, token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), page_size: "20" });
    if (status) params.set("status", status);
    if (search) params.set("search", search);
    const r = await fetch(`${API}/api/v2/reconciliation?${params}`, { headers });
    if (r.ok) { const d = await r.json(); setItems(d.items); setTotal(d.total); }
    setLoading(false);
  }, [page, status, search]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const runBatchAll = async () => {
    setBatchRunning(true);
    const r = await fetch(`${API}/api/v2/reconciliation/batch-all-delivered`, { method: "POST", headers });
    if (r.ok) { const d = await r.json(); toast.success(`Batch: ${d.success} processed, ${d.matched} matched`); fetch_(); }
    else toast.error("Batch failed");
    setBatchRunning(false);
  };

  const statusIcon = (s) => {
    if (s === "MATCHED") return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (s === "PENDING") return <Clock className="w-4 h-4 text-amber-600" />;
    if (s === "ADJUSTED") return <AlertTriangle className="w-4 h-4 text-blue-600" />;
    if (s === "DISPUTED") return <XCircle className="w-4 h-4 text-red-600" />;
    return null;
  };

  return (
    <div data-testid="reconcile-tab" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={runBatchAll} disabled={batchRunning} data-testid="batch-reconcile-btn">
          {batchRunning ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
          Auto-Reconcile All Delivered
        </Button>
        <select className="border rounded px-2 py-1.5 text-sm" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="MATCHED">Matched</option>
          <option value="ADJUSTED">Adjusted</option>
          <option value="DISPUTED">Disputed</option>
        </select>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search AWB or Order ID..." className="w-48" />
        <Button size="sm" variant="outline" onClick={() => { setPage(1); fetch_(); }}><Search className="w-4 h-4" /></Button>
        <span className="text-sm text-gray-500">{total} records</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Order / AWB</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-center">Wt Quoted</th>
              <th className="px-3 py-2 text-center">Wt Actual</th>
              <th className="px-3 py-2 text-center">Quoted Cost</th>
              <th className="px-3 py-2 text-center">Actual Cost</th>
              <th className="px-3 py-2 text-center">Difference</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No reconciliation records. Click "Auto-Reconcile All Delivered" to start.</td></tr>
            ) : items.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-mono font-medium text-gray-900">{r.order_id}</div>
                  <div className="text-xs text-gray-500">{r.awb || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.vendor_code}</td>
                <td className="px-3 py-2 text-center">{r.weight_quoted}kg</td>
                <td className="px-3 py-2 text-center">{r.weight_actual}kg</td>
                <td className="px-3 py-2 text-center">₹{r.quoted_cost}</td>
                <td className="px-3 py-2 text-center">₹{r.actual_cost}</td>
                <td className={`px-3 py-2 text-center font-medium ${r.difference > 0 ? "text-red-600" : r.difference < 0 ? "text-green-600" : "text-gray-500"}`}>
                  {r.difference > 0 ? "+" : ""}₹{r.difference} ({r.difference_percent}%)
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center gap-1 text-xs font-medium">
                    {statusIcon(r.reconciliation_status)} {r.reconciliation_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 20 && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Page {page}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LanesTab({ headers, token }) {
  const [lanes, setLanes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("");

  const fetch_ = useCallback(async () => {
    const params = new URLSearchParams({ page: page.toString(), page_size: "20", sort_by: "shipment_count" });
    if (filter === "profitable") params.set("profitable", "true");
    if (filter === "loss") params.set("profitable", "false");
    if (filter === "review") params.set("needs_review", "true");
    const r = await fetch(`${API}/api/v2/lane-intelligence?${params}`, { headers });
    if (r.ok) { const d = await r.json(); setLanes(d.items); setTotal(d.total); }
  }, [page, filter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const refresh = async () => {
    setRefreshing(true);
    const r = await fetch(`${API}/api/v2/lane-intelligence/refresh`, { method: "POST", headers });
    if (r.ok) { const d = await r.json(); toast.success(d.message); fetch_(); }
    setRefreshing(false);
  };

  return (
    <div data-testid="lanes-tab" className="space-y-4">
      <div className="flex items-center gap-3">
        <Button onClick={refresh} disabled={refreshing} data-testid="refresh-lanes-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh Lanes
        </Button>
        <select className="border rounded px-2 py-1.5 text-sm" value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
          <option value="">All Lanes</option>
          <option value="profitable">Profitable</option>
          <option value="loss">Loss Making</option>
          <option value="review">Needs Review</option>
        </select>
        <span className="text-sm text-gray-500">{total} lanes</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Route</th>
              <th className="px-3 py-2 text-left">Zone</th>
              <th className="px-3 py-2 text-center">Shipments</th>
              <th className="px-3 py-2 text-center">Avg Weight</th>
              <th className="px-3 py-2 text-center">Revenue</th>
              <th className="px-3 py-2 text-center">Cost</th>
              <th className="px-3 py-2 text-center">Profit</th>
              <th className="px-3 py-2 text-center">Margin</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {lanes.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">No lane data. Click "Refresh Lanes" to aggregate from shipments.</td></tr>
            ) : lanes.map(l => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium">{l.origin_city} → {l.destination_city}</div>
                  <div className="text-xs text-gray-400 font-mono">{l.origin_pincode} → {l.destination_pincode}</div>
                </td>
                <td className="px-3 py-2 font-mono text-purple-600">{l.zone_code}</td>
                <td className="px-3 py-2 text-center font-medium">{l.shipment_count}</td>
                <td className="px-3 py-2 text-center">{l.average_weight_kg}kg</td>
                <td className="px-3 py-2 text-center text-green-700">₹{l.revenue?.toLocaleString()}</td>
                <td className="px-3 py-2 text-center text-red-600">₹{l.vendor_cost?.toLocaleString()}</td>
                <td className={`px-3 py-2 text-center font-bold ${l.profit >= 0 ? "text-green-600" : "text-red-600"}`}>₹{l.profit?.toLocaleString()}</td>
                <td className={`px-3 py-2 text-center font-medium ${l.profit_margin_percent >= 15 ? "text-green-600" : l.profit_margin_percent >= 0 ? "text-amber-600" : "text-red-600"}`}>
                  {l.profit_margin_percent}%
                </td>
                <td className="px-3 py-2 text-center">
                  {l.needs_review && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">Review</span>}
                  {l.expansion_opportunity && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Expand</span>}
                  {!l.needs_review && !l.expansion_opportunity && <span className="text-xs text-gray-400">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
